import { z } from "zod";
const scalar = z.union([z.string(), z.number(), z.boolean()]);
export const brewcontrolFieldSchema = z.lazy(() => z.object({
    key: z.string().min(1),
    label: z.string(),
    kind: z.enum(["string", "number", "boolean", "object", "array"]),
    required: z.boolean().optional(),
    default: z.unknown().optional(),
    unit: z.string().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    options: z.array(z.object({ value: scalar, label: z.string() })).optional(),
    fields: z.array(brewcontrolFieldSchema).optional(),
    when: z.object({ key: z.string(), value: scalar }).optional(),
    secret: z.boolean().optional(),
    integer: z.boolean().optional(),
    identity: z.string().optional(),
    columns: z.array(z.string()).optional(),
}));
export const brewcontrolDeviceCatalogSchema = z.record(z.object({
    label: z.string(),
    fields: z.array(brewcontrolFieldSchema),
}));
const brewcontrolUnitFields = {
    unit: z.string(),
    quantity: z.string().nullable().optional(),
    unit_port: z
        .object({ direction: z.enum(["input", "output"]), name: z.string() })
        .optional(),
};
export const brewcontrolPortSchema = z.object({
    name: z.string(),
    label: z.string().optional(),
    required: z.boolean().optional(),
    ...brewcontrolUnitFields,
    /** Sign labels of a bipolar output: negative then positive. */
    signs: z
        .object({ negative: z.string(), positive: z.string() })
        .nullable()
        .optional(),
    /**
     * An output the node computes but sends to no device, so its alias names a
     * run-local signal and no exposure can back it. An operator may still drive
     * it in manual, and the node still publishes it under `outputs.`.
     */
    internal: z.boolean().optional(),
    /**
     * A port bound to a pump: the node converts its speed with the pump's entry
     * in the CALIBRATION document, so the bound device signal needs one.
     */
    calibrated: z.boolean().optional(),
});
export const brewcontrolDescriptorSchema = z.object({
    name: z.string(),
    label: z.string().optional(),
    scalar_type: z.enum(["number", "integer", "boolean"]).optional(),
    nullable: z.boolean().optional(),
    ...brewcontrolUnitFields,
    /** Integer value → label; empty when the field is not an enum. */
    enum_values: z.record(z.string()).optional(),
});
/** A local reference in a controller's own vocabulary, externally tagged by kind. */
export const brewcontrolRefSchema = z.union([
    z.object({ input: z.string() }).strict(),
    z.object({ setpoint: z.string() }).strict(),
    z.object({ output: z.string() }).strict(),
    z.object({ state: z.string() }).strict(),
]);
const brewcontrolReadingSchema = z
    .object({
    of: brewcontrolRefSchema,
    label: z.string().nullable().optional(),
    /** A one-line "label value" instead of a labelled number. */
    small: z.boolean().optional(),
})
    .strict();
const brewcontrolStatusSchema = z
    .object({
    of: brewcontrolRefSchema,
    cases: z.array(z
        .object({
        variant: z.string(),
        text: z.string(),
        reading: brewcontrolRefSchema.nullable().optional(),
    })
        .strict()),
})
    .strict();
/**
 * What the node says is worth showing for a controller. Same shape at type
 * level and as a wiring override. Each `readings` entry is a row.
 */
export const brewcontrolShowSchema = z
    .object({
    headline: brewcontrolRefSchema.nullable().optional(),
    detail: brewcontrolRefSchema.nullable().optional(),
    target: brewcontrolRefSchema.nullable().optional(),
    actuator: brewcontrolRefSchema.nullable().optional(),
    readings: z.array(z.array(brewcontrolReadingSchema)).optional(),
    status: brewcontrolStatusSchema.nullable().optional(),
    cascade: z
        .object({
        demand: brewcontrolRefSchema,
        stages: z.array(z.object({ output: z.string(), config: z.string() }).strict()),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
export function emptyShow() {
    return {
        headline: null,
        detail: null,
        target: null,
        actuator: null,
        readings: [],
        status: null,
        cascade: null,
    };
}
/** Wiring `show` replaces the single-valued fields; its reading rows follow the type's. */
export function mergeShow(typeLevel, override) {
    const parsed = brewcontrolShowSchema.safeParse(override);
    const wired = parsed.success ? parsed.data : {};
    const base = typeLevel ?? {};
    return {
        headline: wired.headline ?? base.headline ?? null,
        detail: wired.detail ?? base.detail ?? null,
        target: wired.target ?? base.target ?? null,
        actuator: wired.actuator ?? base.actuator ?? null,
        readings: [...(base.readings ?? []), ...(wired.readings ?? [])],
        status: wired.status ?? base.status ?? null,
        cascade: wired.cascade ?? base.cascade ?? null,
    };
}
/** Action names, titles and payload fields from the catalog's action enum schema. */
export function catalogActions(actions) {
    const parsed = z
        .object({
        oneOf: z
            .array(z
            .object({
            title: z.string().optional(),
            properties: z
                .record(z
                .object({
                const: z.string().optional(),
                title: z.string().optional(),
            })
                .passthrough())
                .optional(),
        })
            .passthrough())
            .optional(),
    })
        .passthrough()
        .safeParse(actions);
    if (!parsed.success || !parsed.data.oneOf)
        return [];
    return parsed.data.oneOf.flatMap((variant) => {
        const name = variant.properties?.command?.const;
        if (!name)
            return [];
        const fields = Object.entries(variant.properties ?? {})
            .filter(([key]) => key !== "command")
            .map(([key, schema]) => ({ name: key, title: schema.title ?? key }));
        return [{ name, title: variant.title ?? name, fields }];
    });
}
export const brewcontrolControllerCatalogSchema = z.record(z.object({
    label: z.string().optional(),
    description: z.string().optional(),
    show: brewcontrolShowSchema.optional(),
    actions: z.unknown().optional(),
    inputs: z.array(brewcontrolPortSchema),
    setpoints: z.array(brewcontrolPortSchema),
    outputs: z.array(brewcontrolPortSchema),
    state: z.array(z.string()),
    state_descriptors: z.array(brewcontrolDescriptorSchema).optional(),
    mode: brewcontrolDescriptorSchema.optional(),
    inputs_open: z.boolean().optional(),
    /** Every open input that is not a declared port is a calibrated pump. */
    inputs_open_calibrated: z.boolean().optional(),
    setpoints_open: z.boolean().optional(),
    config_schema: z
        .object({ properties: z.record(z.unknown()).optional() })
        .passthrough()
        .optional(),
    config_defaults: z.record(z.unknown()).optional(),
}));
/** Whether the node's build reads pump entries in CALIBRATION; older builds refuse the document. */
export function supportsPumpCalibration(catalog) {
    return Object.values(catalog).some((type) => [...type.inputs, ...type.outputs].some((port) => port.calibrated !== undefined));
}
export function readCatalog(schema, value) {
    const result = schema.safeParse(value);
    return result.success ? result.data : null;
}
/**
 * The calibration routines a node's build offers, as sent at Hello. The two
 * schemas are raw JSON Schema (schemars output): a params document to launch
 * with and the shape of `RoutineFinished.result_json`.
 */
export const brewcontrolRoutineCatalogSchema = z.record(z.object({
    label: z.string(),
    description: z.string().optional(),
    params_schema: z
        .object({ properties: z.record(z.unknown()).optional() })
        .passthrough(),
    result_schema: z.object({}).passthrough(),
}));
//# sourceMappingURL=brewcontrol-catalog.js.map